import { BellRing } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Linking, StyleSheet, Switch, View } from 'react-native';

import { Overline } from '@/components/Overline';
import { Text } from '@/components/Text';
import { colors, spacing } from '@/constants/theme';
import {
  getPushPermission,
  type PushPermission,
  registerForPushNotificationsAsync,
} from '@/services/pushNotifications';
import { usersService } from '@/services/users';
import { authStore } from '@/stores/auth.store';

/**
 * Profile push toggle. Reflects real deliverability (server master flag AND OS
 * permission) and drives real registration on enable — not just a flag flip:
 *   ON  → request OS permission if needed, register the token, PATCH push_enabled=true.
 *         If the OS permission is hard-denied, offer to open iOS Settings.
 *   OFF → PATCH push_enabled=false (server stops sending; token kept so
 *         re-enable is instant).
 */
export const NotificationSetting = observer(() => {
  const user = authStore.user;
  const [permission, setPermission] = useState<PushPermission>('undetermined');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setPermission(await getPushPermission());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const on = Boolean(user?.push_enabled) && permission === 'granted';

  async function setEnabled(next: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      if (next) {
        const result = await registerForPushNotificationsAsync({ promptIfNeeded: true });
        if (result.status === 'granted') {
          await usersService.setPushEnabled(true);
          await authStore.refreshMe();
        } else if (result.status === 'denied') {
          // Hard-denied at the OS level — a toggle can't re-prompt; send them to
          // Settings to flip it there.
          promptOpenSettings();
        }
      } else {
        await usersService.setPushEnabled(false);
        await authStore.refreshMe();
      }
    } catch {
      // Transient failure — leave UI state as-is rather than stranding it.
    } finally {
      await refresh();
      setBusy(false);
    }
  }

  return (
    <View style={styles.section}>
      <Overline>Notifications</Overline>
      <View style={styles.card}>
        <View style={styles.row}>
          <View style={styles.label}>
            <BellRing size={16} color={colors.textMuted} />
            <Text variant="body">Push notifications</Text>
          </View>
          <Switch
            value={on}
            onValueChange={setEnabled}
            disabled={busy}
            trackColor={{ true: colors.accent, false: colors.track }}
            testID="push-toggle"
          />
        </View>
        {permission === 'denied' && (
          <View style={styles.hint}>
            <Text variant="caption" color="textMuted">
              Turned off in iOS Settings. Flip the switch to open Settings and
              re-enable.
            </Text>
          </View>
        )}
      </View>
    </View>
  );
});

function promptOpenSettings() {
  Alert.alert(
    'Turn on notifications',
    'Notifications are disabled for MarketPryce in iOS Settings. Open Settings to turn them back on.',
    [
      { text: 'Not now', style: 'cancel' },
      { text: 'Open Settings', onPress: () => void Linking.openSettings() },
    ],
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.base,
    minHeight: 52,
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  hint: {
    paddingHorizontal: spacing.base,
    paddingBottom: spacing.base,
  },
});
